'use client' // This ensures the component runs in the client-side context

import axios from 'axios'
import { useEffect } from 'react'
import { OpenAPI } from '../../client/core/OpenAPI'
import { isAiClaimsPortalPath } from '../config/aiclaims-routes'
import {
  defaultBrandFromHostname,
} from '../lib/brandFromHost'
import { isWysehubPublicEntryPath } from '../lib/hostRouting'
import { isAiClaimsPortalHostname } from '../lib/managedHosts'
import { sanitizeInternalRedirect } from '../utils/safeRedirect'

const GlobalProvider = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    const previousBase = OpenAPI.BASE
    const previousWithCredentials = OpenAPI.WITH_CREDENTIALS

    OpenAPI.HEADERS = async (): Promise<Record<string, string>> => {
      if (typeof window === 'undefined') return {}
      const brand = defaultBrandFromHostname(
        window.location.hostname,
        process.env.NEXT_PUBLIC_AICLAIMS_HOST,
        process.env.NEXT_PUBLIC_AICLAIMS_PORTAL_HOST,
      )
      return { 'X-Default-Brand': brand }
    }
    OpenAPI.BASE = ''
    OpenAPI.WITH_CREDENTIALS = true

    // Log the value of NEXT_PUBLIC_API_URL to ensure it's being read correctly
    //console.log('API URL:', process.env.NEXT_PUBLIC_API_URL);

    //axios.defaults.baseURL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost';
    axios.defaults.withCredentials = true

    // Set the OpenAPI base URL using the environment variable
    // Note: With the new generator, configuration is handled per API instance
    // Global configuration can be set via Configuration class if needed

    // Note: With the new OpenAPI generator, authentication is handled via cookies
    // through axios.defaults.withCredentials = true (set above)
    // Individual API instances can be configured with custom Configuration if needed

    // Add response interceptor to handle authentication errors globally
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log(
            `🔐 Authentication error detected (${error.response.status}): ${error.response?.detail || 'Session expired'}`,
          )

          // Clear authentication data
          localStorage.removeItem('access_token')
          localStorage.removeItem('effectivePartnerType')

          // Only redirect if we're not already on the login page, homepage,
          // or other public entry routes (e.g. password recovery hits /users/me → 401)
          const currentPath = window.location.pathname
          if (
            currentPath !== '/' &&
            !currentPath.includes('login') &&
            !isWysehubPublicEntryPath(currentPath)
          ) {
            console.log('🚪 Redirecting to login due to authentication failure')
            // Store the current URL for redirect after login
            const safePath = sanitizeInternalRedirect(
              `${window.location.pathname}${window.location.search}${window.location.hash}`,
              window.location.origin,
            )
            if (safePath) {
              localStorage.setItem('loginRedirectUrl', safePath)
            }
            // Redirect to homepage with login modal
            window.location.href = '/?login=true'
          }
        }

        if (error.response?.status === 403) {
          console.log(
            `🚫 Forbidden error detected (${error.response.status}): ${error.response?.detail || 'Access denied'}`,
          )

          const requestUrl = String(error.config?.url ?? '')
          if (requestUrl.includes('/users/me/active-brand')) {
            return Promise.reject(error)
          }

          const hostname =
            typeof window !== 'undefined'
              ? window.location.hostname
              : ''
          const isAiClaimsPortalHost = isAiClaimsPortalHostname(hostname)
          const isAiClaimsPath = isAiClaimsPortalPath(window.location.pathname)

          if (isAiClaimsPortalHost && isAiClaimsPath) {
            if (window.location.pathname !== '/unauthorised') {
              window.location.href = '/unauthorised'
            }
          } else if (window.location.pathname !== '/unauthorised') {
            window.location.href = '/unauthorised'
          }
        }

        return Promise.reject(error)
      },
    )

    // Note: Authentication error handling is now managed through the axios interceptor above
    // The new OpenAPI generator uses axios internally, so the interceptor covers all API calls

    // Cleanup function to remove interceptors when component unmounts
    return () => {
      OpenAPI.HEADERS = undefined
      OpenAPI.BASE = previousBase
      OpenAPI.WITH_CREDENTIALS = previousWithCredentials
      axios.interceptors.response.eject(responseInterceptor)
    }
  }, [])

  return <>{children}</>
}

export default GlobalProvider
